/**
 * AdminLabPCs — cyber-café / ICT-lab PC control surface.
 * Route: /admin/lab  (admin auth).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Monitor, Lock, Unlock, Play, Square, Plus, Trash2, Copy, KeyRound, RefreshCw } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const PCCard = ({ pc, onStart, onEnd, onLock, onUnlock, onRotate, onDelete }) => (
  <Card className="bg-slate-900/60 border-slate-800" data-testid={`lab-pc-${pc.id}`}>
    <CardHeader className="pb-2">
      <div className="flex items-start justify-between">
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Monitor className={`h-4 w-4 ${pc.online ? 'text-emerald-300' : 'text-slate-500'}`} />
          {pc.name}
        </CardTitle>
        <div className="flex gap-1.5">
          {pc.online
            ? <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/40">online</Badge>
            : <Badge variant="outline" className="text-slate-500">offline</Badge>}
          <Badge variant="outline" className={pc.lock_state === 'locked' ? 'text-rose-300' : 'text-emerald-300'}>
            {pc.lock_state === 'locked' ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
            {pc.lock_state}
          </Badge>
        </div>
      </div>
      <div className="text-xs text-slate-500 mt-1">{pc.location} · {pc.currency} {Number(pc.price_per_hour).toFixed(2)}/hr</div>
    </CardHeader>
    <CardContent className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {pc.active_session_id
          ? <Button size="sm" variant="destructive" onClick={() => onEnd(pc.id)} data-testid={`lab-pc-end-${pc.id}`}><Square className="h-3.5 w-3.5 mr-1" /> End</Button>
          : <Button size="sm" onClick={() => onStart(pc)} data-testid={`lab-pc-start-${pc.id}`}><Play className="h-3.5 w-3.5 mr-1" /> Start</Button>}
        {pc.lock_state === 'unlocked'
          ? <Button size="sm" variant="outline" className="border-slate-700 text-slate-200" onClick={() => onLock(pc.id)}><Lock className="h-3.5 w-3.5 mr-1" /> Lock</Button>
          : <Button size="sm" variant="outline" className="border-slate-700 text-slate-200" onClick={() => onUnlock(pc.id)}><Unlock className="h-3.5 w-3.5 mr-1" /> Unlock</Button>}
      </div>
      <div className="flex gap-1.5 text-xs">
        <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => onRotate(pc)}>
          <KeyRound className="h-3 w-3 mr-1" /> Rotate token
        </Button>
        <Button size="sm" variant="ghost" className="text-slate-500 hover:text-rose-300 ml-auto" onClick={() => onDelete(pc.id)}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

const AdminLabPCs = () => {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', location: '', price_per_hour: 1.0, currency: 'USD' });
  const [startTarget, setStartTarget] = useState(null);
  const [startForm, setStartForm] = useState({ customer_name: '', duration_minutes: 60, payment_method: 'cash' });
  const [tokenShown, setTokenShown] = useState(null);

  const { data: pcs = [], refetch } = useQuery({
    queryKey: ['lab-pcs'],
    queryFn: async () => (await axios.get(`${API}/api/lab/pcs`, { headers: authH() })).data,
    refetchInterval: 10000,
  });

  const m = {
    create: useMutation({ mutationFn: async () => (await axios.post(`${API}/api/lab/pcs`, form, { headers: authH() })).data,
      onSuccess: (d) => { toast.success('PC registered'); setAdding(false); setTokenShown({ pc_name: d.name, agent_token: d.agent_token });
        qc.invalidateQueries({ queryKey: ['lab-pcs'] }); },
      onError: (e) => toast.error(e?.response?.data?.detail || 'Failed') }),
    del: useMutation({ mutationFn: async (id) => axios.delete(`${API}/api/lab/pcs/${id}`, { headers: authH() }),
      onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['lab-pcs'] }); } }),
    lock: useMutation({ mutationFn: async (id) => axios.post(`${API}/api/lab/pcs/${id}/lock`, {}, { headers: authH() }),
      onSuccess: () => { toast.success('Locked'); qc.invalidateQueries({ queryKey: ['lab-pcs'] }); } }),
    unlock: useMutation({ mutationFn: async (id) => axios.post(`${API}/api/lab/pcs/${id}/unlock`, {}, { headers: authH() }),
      onSuccess: () => { toast.success('Unlocked'); qc.invalidateQueries({ queryKey: ['lab-pcs'] }); } }),
    start: useMutation({ mutationFn: async () => (await axios.post(`${API}/api/lab/pcs/${startTarget.id}/start`, startForm, { headers: authH() })).data,
      onSuccess: () => { toast.success('Session started'); setStartTarget(null); qc.invalidateQueries({ queryKey: ['lab-pcs'] }); },
      onError: (e) => toast.error(e?.response?.data?.detail || 'Failed') }),
    end: useMutation({ mutationFn: async (id) => (await axios.post(`${API}/api/lab/pcs/${id}/end`, {}, { headers: authH() })).data,
      onSuccess: (d) => { toast.success(`Session ended · ${d.currency} ${Number(d.actual_total).toFixed(2)} via POS`);
        qc.invalidateQueries({ queryKey: ['lab-pcs'] }); },
      onError: (e) => toast.error(e?.response?.data?.detail || 'Failed') }),
    rotate: useMutation({ mutationFn: async (pc) => {
        const { data } = await axios.post(`${API}/api/lab/pcs/${pc.id}/rotate-token`, {}, { headers: authH() });
        return { pc_name: pc.name, agent_token: data.agent_token };
      }, onSuccess: (d) => setTokenShown(d) }),
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white inline-flex items-center gap-2">
              <Monitor className="h-6 w-6 text-indigo-400" /> Lab / Cyber-café PCs
            </h1>
            <p className="text-sm text-slate-400 mt-1">Ending a session creates a POS sale automatically (visible in the register Z-report).</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}><RefreshCw className="h-4 w-4" /></Button>
            <Button onClick={() => setAdding(true)} data-testid="lab-pc-add"><Plus className="h-4 w-4 mr-1" /> Add PC</Button>
          </div>
        </header>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="lab-pc-grid">
          {pcs.map(pc => (
            <PCCard key={pc.id} pc={pc}
                     onStart={(p) => { setStartTarget(p); setStartForm({ customer_name: '', duration_minutes: 60, payment_method: 'cash' }); }}
                     onEnd={(id) => window.confirm('End session and bill via POS?') && m.end.mutate(id)}
                     onLock={(id) => m.lock.mutate(id)}
                     onUnlock={(id) => m.unlock.mutate(id)}
                     onRotate={(p) => m.rotate.mutate(p)}
                     onDelete={(id) => window.confirm('Delete this PC?') && m.del.mutate(id)} />
          ))}
          {pcs.length === 0 && <div className="text-slate-500 col-span-full text-center py-10">No PCs yet — click <strong>Add PC</strong>.</div>}
        </div>

        <Dialog open={adding} onOpenChange={setAdding}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
            <DialogHeader><DialogTitle>Register a lab PC</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">An agent token will be shown once for the polling agent.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-slate-800/60 border-slate-700" data-testid="lab-pc-new-name" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Location</Label>
                <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="bg-slate-800/60 border-slate-700" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Price / hour</Label>
                <Input type="number" step="0.1" value={form.price_per_hour}
                       onChange={e => setForm({ ...form, price_per_hour: Number(e.target.value) })}
                       className="bg-slate-800/60 border-slate-700" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
              <Button onClick={() => m.create.mutate()} disabled={m.create.isPending || !form.name} data-testid="lab-pc-save">
                {m.create.isPending ? 'Saving…' : 'Register'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!startTarget} onOpenChange={(o) => !o && setStartTarget(null)}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
            <DialogHeader>
              <DialogTitle>Start · {startTarget?.name}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Total ≈ {startTarget?.currency} {(Number(startTarget?.price_per_hour || 0) * (startForm.duration_minutes / 60)).toFixed(2)}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500">Customer (optional)</Label>
                <Input value={startForm.customer_name} onChange={e => setStartForm({ ...startForm, customer_name: e.target.value })} className="bg-slate-800/60 border-slate-700" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Duration (minutes)</Label>
                <Input type="number" min={1} max={720} value={startForm.duration_minutes}
                       onChange={e => setStartForm({ ...startForm, duration_minutes: Number(e.target.value) })}
                       className="bg-slate-800/60 border-slate-700" data-testid="lab-pc-start-duration" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStartTarget(null)}>Cancel</Button>
              <Button onClick={() => m.start.mutate()} disabled={m.start.isPending} data-testid="lab-pc-start-confirm">
                {m.start.isPending ? 'Starting…' : 'Start'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!tokenShown} onOpenChange={(o) => !o && setTokenShown(null)}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
            <DialogHeader>
              <DialogTitle>Agent token · {tokenShown?.pc_name}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Shown ONCE. Paste into the PC&apos;s agent config — cannot be retrieved later, only rotated.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-slate-950 border border-slate-800 rounded p-3 font-mono text-emerald-300 text-sm break-all">
              {tokenShown?.agent_token}
            </div>
            <DialogFooter>
              <Button onClick={() => { navigator.clipboard.writeText(tokenShown?.agent_token || ''); toast.success('Copied'); }}>
                <Copy className="h-4 w-4 mr-1" /> Copy
              </Button>
              <Button variant="outline" onClick={() => setTokenShown(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminLabPCs;
